import { pageTitle } from '@palaccount/config';
import { SEE_ITEM_COST, type OrderResponse } from '@palaccount/contracts';
import { FileInput, Plus, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { DeleteConfirmBox } from '@/components/confirm-box';
import { useUnsavedChanges } from '@/components/document-header-controls';
import { DocumentToolbar } from '@/components/document-toolbar';
import { useLineImport } from '@/features/documents/line-tools';
import { DocumentTotals, TwoWayPair } from '@/features/documents/document-totals';
import { usePriceTiers } from '@/features/inventory/api';
import { LastPriceHint } from '@/features/documents/last-price-hint';
import { DescriptionLangToggle, useDescriptionLanguage } from '@/features/documents/description-language';
import { AttachmentsPanel } from '@/features/attachments/attachments-panel';
import { DocumentActions } from '@/features/documents/document-actions';
import { salesOrderDocument } from '@/features/documents/record-documents';
import { OrderStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ItemCell } from '@/features/inventory/item-cell';
import { useItemPicker } from '@/features/inventory/item-picker';
import { useWarehouses } from '@/features/inventory/stock-api';
import { PartnerBalanceChip } from '@/features/partners/partner-balance-chip';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { useOrgSetting } from '@/features/admin/api';
import { denyPermission, useCan } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useBaseCurrency, useDocumentDate } from '@/lib/session';

import { QuotationConvertPicker } from './copy-from-modal';
import { BelowCostHint, LineStockHint, OverStockStrip, belowCost, shortBy } from './line-stock-warning';
import { applicableTierId, tierColumns, tierPriceFor } from './quote-tiers';
import { MarginPanel, netAmount } from './margin-panel';
import { OrderActionButtons, OrderDialogs, useOrderActions } from './order-actions';
import { OrderLifecycle } from './order-lifecycle';
import { useCreateOrder, useDeleteOrder, useOrder, useUpdateOrder } from './orders-api';
import { useConvertPickedQuotation } from './quotes-api';
import {
  LineUnitSelect,
  SalesHeaderFields,
  salesHeaderCreateBody,
  salesHeaderFromDoc,
  salesHeaderPatchBody,
  useLineColumns,
  useSalesHeaderState,
} from './sales-header-fields';
import { useRateInput } from '@/features/documents/rate-field';
import { useDocCurrency } from '@/features/documents/doc-currency';
import { CollapsibleHeaderCard } from '@/features/documents/header-card';
import { useCommissionInput } from '@/features/sales/commission-field';
import { computeTotalsMirror, type TwoWayState } from './totals-mirror';

interface GridLine {
  key: number;
  itemId: string;
  /** The reservation's address — confirming reserves item+warehouse lines. */
  warehouseId: string;
  /** INV-L4 (SO-02): the unit the qty is typed in; '' = the item's stock unit. */
  unitId: string;
  description: string;
  qty: string;
  /** INV-L1: the free quantity beside qty; '' = none. */
  bonusQty: string;
  unitPrice: string;
  discountPct: string;
  /** Wave 4B: the line's own delivery date for a staged order; '' = the header's expected date. */
  deliveryDate: string;
  /**
   * BR-MD-03-14's "already typed" — a fact about the CELL, set by its own
   * `onChange` and by nothing else. The reasoning, and the three cases that
   * rule out the two simpler tests ("is it empty" and "does it still hold
   * what the pick put there"), is written out once on the same field in
   * `sales/invoice-editor-page.tsx`.
   */
  typedDescription: boolean;
  typedUnitPrice: boolean;
}

let nextKey = 1;
const blankLine = (): GridLine => ({
  key: nextKey++,
  itemId: '',
  warehouseId: '',
  unitId: '',
  description: '',
  qty: '',
  bonusQty: '',
  unitPrice: '',
  discountPct: '',
  deliveryDate: '',
  typedDescription: false,
  typedUnitPrice: false,
});

/** INV-L1: a stored bonus of 0.000 is an empty cell, not a zero to read. */
function bonusText(stored: string | undefined): string {
  // Tolerates a line written before the column existed (and older fixtures).
  if (stored === undefined || /^0+(\.0+)?$/.test(stored)) return '';
  return stored.includes('.') ? stored.replace(/\.?0+$/, '') : stored;
}

const MONEY_RE = /^\d{1,13}(\.\d{1,2})?$/;
const PCT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const QTY_RE = /^\d{1,12}(\.\d{1,3})?$/;

function fromOrder(order: OrderResponse): GridLine[] {
  const lines = order.lines.map((line) => ({
    key: nextKey++,
    itemId: line.itemId ?? '',
    warehouseId: line.warehouseId ?? '',
    unitId: line.unitId ?? '',
    description: line.description,
    qty: line.qty,
    bonusQty: bonusText(line.bonusQty),
    unitPrice: line.unitPrice,
    discountPct: line.discountPct,
    // Older fixtures and rows written before the column carry none.
    deliveryDate: line.deliveryDate ?? '',
    // A saved line's text and price are somebody's typed work.
    typedDescription: true,
    typedUnitPrice: true,
  }));
  return lines.length === 0 ? [blankLine()] : lines;
}

function CellInput({ mono, className, ...props }: React.ComponentProps<'input'> & { mono?: boolean }) {
  return (
    <input
      className={cn(
        'h-9 w-full min-w-0 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none transition-colors',
        'hover:border-input focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-70',
        mono && 'text-end font-mono tabular-nums',
        className,
      )}
      {...props}
    />
  );
}

/**
 * SAL-02's order EDITOR — where a draft order's lines are actually typed
 * (the list-and-detail screen shows them read-only; "edit them on the
 * document screen" is the spec's own §15 answer). Draft only: a confirmed
 * order's commercial content is frozen (R8) and only the expected date and
 * notes stay live — until Amend (wave 4B) reopens it as a draft.
 *
 * Wave 4B: the form carries the order's whole flow, not only its fields —
 * the list page's acts (Confirm with the rate and credit questions, Create
 * delivery note, Create invoice, Amend, Cancel) and its place in the chain
 * (linked chips, the stepper, the progress line), drawn by the SAME
 * components the list page uses (`order-actions.tsx`, `order-lifecycle.tsx`).
 */
export function OrderEditorPage() {
  const { t, i18n } = useTranslation();
  const printLocale = i18n.language.startsWith('ar') ? ('ar' as const) : ('en' as const);
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const isNew = !orderId;

  const { data: order, isPending: loading } = useOrder(orderId);
  const { data: warehouses } = useWarehouses();
  const createOrder = useCreateOrder();
  const updateOrder = useUpdateOrder(orderId ?? '');
  const deleteOrder = useDeleteOrder(orderId ?? '');

  const canEdit = useCan(isNew ? 'sales.create' : 'sales.edit');

  const [partnerId, setPartnerId] = useState('');
  const [orderDate, setOrderDate] = useDocumentDate();
  // G-09: every date opens at the company's today — a saved order's own
  // expected date (or its absence) replaces the seed below.
  const [expectedDate, setExpectedDate] = useDocumentDate();
  /** INV-F1…F6 (SO-02) — the shared sales header; a new document's salesperson is me. */
  const [header, setHeader] = useSalesHeaderState(isNew);
  // Wave 4D — the editable rate (features/documents/rate-field).
  const rate = useRateInput(order);
  // Wave 4D — the commission % (features/sales/commission-field).
  const commission = useCommissionInput(order);
  const { bonusOn, discountOn } = useLineColumns();
  const [lines, setLines] = useState<GridLine[]>([blankLine()]);
  const [discount, setDiscount] = useState<TwoWayState | undefined>(undefined);
  const [tax, setTax] = useState<TwoWayState | undefined>(undefined);
  const [notes, setNotes] = useState('');
  /** Wave 4B: the customer's reference / PO number and the order's terms. */
  const [reference, setReference] = useState('');
  const [terms, setTerms] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** Wave 4B — the "From quotation…" picker, a NEW order only. */
  const [quotePicking, setQuotePicking] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const convertQuotation = useConvertPickedQuotation();
  /** Wave 4B — the list page's acts, on the form (`order-actions.tsx`). */
  const acts = useOrderActions(orderId ?? '', 'editor');
  const loadedId = useRef<string | null>(null);
  /** S4 — the header rail's ‹‹ ›› wait while there is typed work (see `useUnsavedChanges`). */
  const dirty = useUnsavedChanges(
    [partnerId, orderDate, expectedDate, header, lines, discount, tax, notes, reference, terms],
    isNew ? 'new' : order && loadedId.current === order.id ? order.id : null,
  );

  const status = order?.status ?? 'draft';
  const isDraft = status === 'draft';
  const editable = isDraft && canEdit;
  /** Post-confirm, the shipping estimate and notes stay live (SAL-02). */
  const liveOnly = !isDraft && status !== 'cancelled' && canEdit;

  useEffect(() => {
    document.title = pageTitle(t('orders.editorTitle'));
  }, [t]);

  useEffect(() => {
    if (order && loadedId.current !== order.id) {
      loadedId.current = order.id;
      setPartnerId(order.partnerId);
      setOrderDate(order.orderDate);
      setExpectedDate(order.expectedDate ?? '');
      setHeader(salesHeaderFromDoc(order));
      setLines(fromOrder(order));
      setDiscount({ basis: 'pct', value: order.discountPct });
      setTax({ basis: 'pct', value: order.taxPct });
      setNotes(order.notes ?? '');
      setReference(order.reference ?? '');
      setTerms(order.terms ?? '');
    }
  }, [order]);

  /**
   * Wave 4B — "From quotation…": the LINKING conversion (the quotation's own
   * Convert to Order route), then the new order opens. Nothing typed on this
   * blank form is kept: the order IS the quotation's.
   */
  const onConvertQuotation = async (quotationId: string) => {
    setQuoteError(null);
    try {
      const result = await convertQuotation.mutateAsync(quotationId);
      setQuotePicking(false);
      void navigate(`/sales/orders/${result.orderId}/edit`, { replace: true });
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.create');
      setQuoteError(localizeServerCause(t, thrown, 'orders.errSaveFailedEditor'));
    }
  };

  // GEN-50 — one panel of matches, plus the records these lines already name.
  const customerPicker = usePartnerPicker(partnerId || null, { type: 'customer' });
  const itemPicker = useItemPicker(
    useMemo(() => lines.map((line) => line.itemId), [lines]),
    { status: 'active' },
  );

  const partner = customerPicker.byId.get(partnerId);
  // GEN-108's chain, ending where it should: the DOCUMENT's own code, else
  // the party's usual currency, else the company's base (GEN-107) read from
  // the session — never a literal, which printed dollars on a euro
  // company's every new document until a party was picked.
  const base = useBaseCurrency();
  /**
   * Wave 4C — BR-SAL-01-05's tier pricing, as the quotation applies it: a
   * picked (or imported) item fills at the price of the tier that applies to
   * THIS customer (theirs, else the company default), the item's unit price
   * as the fallback. A reader whose role cannot list tiers gets the unit price.
   */
  const tiersQuery = usePriceTiers(false);
  const saleTier = useMemo(
    () => applicableTierId(partner?.priceTierId, tierColumns(tiersQuery.data ?? [])),
    [partner, tiersQuery.data],
  );
  // Per-document currency (2026-10-30): the header's dropdown in front of the chain.
  const docCurrency = useDocCurrency({
    partnerId,
    savedCurrency: order?.currency,
    partnerCurrency: partner?.currency ?? base,
    onPick: () => rate.setText(''),
  });
  const currency = docCurrency.currency;
  /** An order converted from a quotation keeps the quotation's currency. */
  const currencyLocked = !editable || (order?.sourceQuotationId ?? null) !== null;

  // EC-19-73: the tax box opens at the COMPANY default VAT rate.
  const defaultTaxPct = useOrgSetting<string>('invoicing.default_vat_pct');
  const totals = useMemo(
    () => computeTotalsMirror(lines, discount, tax, currency, defaultTaxPct),
    [lines, discount, tax, currency, defaultTaxPct],
  );

  const setLine = (key: number, patch: Partial<GridLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  /**
   * INV-L6 / INV-L7 — the invoice grid's two warnings, on the order too (see
   * `line-stock-warning.tsx`): stock is read at the line's reservation
   * warehouse, cost only for a reader allowed to see it. Warn, never block.
   */
  const lineShort = (line: GridLine): string | null =>
    editable && line.unitId === ''
      ? shortBy(itemPicker.byId.get(line.itemId), line.qty, {
          // INV-L3: a line without its own warehouse ships from the header's.
          warehouseId: line.warehouseId || header.warehouseId,
        })
      : null;
  const anyShort = lines.some((line) => lineShort(line) !== null);
  const canSeeCost = useCan(SEE_ITEM_COST);
  const lineBelowCost = (line: GridLine): string | null =>
    editable && canSeeCost
      ? belowCost(line.unitPrice, itemPicker.byId.get(line.itemId)?.costPrice)
      : null;

  /** BR-MD-03-14: picking fills name and price; it never overwrites typed work. */
  const pickItem = (key: number, id: string) => {
    const item = itemPicker.byId.get(id);
    if (!item) {
      setLine(key, { itemId: '' });
      return;
    }
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              itemId: id,
              // INV-L4: the picked unit belonged to the OLD item.
              unitId: '',
              // Per FIELD, not per line: a typed description must not stop
              // the untouched price filling.
              description: line.typedDescription ? line.description : descLang.describe(item),
              unitPrice: line.typedUnitPrice ? line.unitPrice : tierPriceFor(item, saleTier),
            }
          : line,
      ),
    );
  };

  const removeLine = (key: number) => {
    setLines((current) => {
      const remaining = current.filter((line) => line.key !== key);
      return remaining.length === 0 ? [blankLine()] : remaining;
    });
  };

  const usedLines = lines.filter(
    (line) => line.description.trim() !== '' || line.qty.trim() !== '' || line.unitPrice.trim() !== '',
  );

  const validate = (): string | null => {
    if (!partnerId) return t('sales.errNoCustomer');
    // SAL-02 §15 "Empty": a draft order MAY have no lines — used rows must
    // still be whole.
    for (const [index, line] of usedLines.entries()) {
      if (line.description.trim() === '') return t('sales.errLineDescription', { line: index + 1 });
      if (!QTY_RE.test(line.qty.trim()) || /^0+(\.0+)?$/.test(line.qty.trim()))
        return t('sales.errLineQty', { line: index + 1 });
      if (!MONEY_RE.test(line.unitPrice.trim())) return t('sales.errLinePrice', { line: index + 1 });
      const bonus = line.bonusQty.trim();
      if (bonusOn && bonus !== '' && !QTY_RE.test(bonus)) return t('sales.errLineBonus', { line: index + 1 });
      const pct = line.discountPct.trim();
      if (discountOn && pct !== '' && !PCT_RE.test(pct)) return t('sales.errDiscountRange');
    }
    if (discount && !(discount.basis === 'pct' ? PCT_RE : MONEY_RE).test(discount.value.trim()))
      return t('sales.errDiscountRange');
    if (tax && !(tax.basis === 'pct' ? PCT_RE : MONEY_RE).test(tax.value.trim()))
      return t('sales.errTaxRange');
    return null;
  };

  const buildTwoWay = (state: TwoWayState | undefined) =>
    state === undefined
      ? undefined
      : state.basis === 'pct'
        ? { pct: state.value.trim() }
        : { amount: state.value.trim() };

  // The invoice grid's line shape, on the order (SO-02): INV-L3's
  // header-default warehouse, INV-L4's unit, INV-L1's bonus, INV-L2's gate.
  const requestLines = () =>
    usedLines.map((line) => {
      const warehouseId = line.warehouseId || header.warehouseId;
      return {
        ...(line.itemId ? { itemId: line.itemId } : {}),
        ...(line.itemId && warehouseId ? { warehouseId } : {}),
        ...(line.itemId && line.unitId ? { unitId: line.unitId } : {}),
        description: line.description.trim(),
        qty: line.qty.trim(),
        ...(bonusOn && line.bonusQty.trim() !== '' ? { bonusQty: line.bonusQty.trim() } : {}),
        unitPrice: line.unitPrice.trim(),
        ...(discountOn && line.discountPct.trim() !== ''
          ? { discountPct: line.discountPct.trim() }
          : {}),
        ...(line.deliveryDate ? { deliveryDate: line.deliveryDate } : {}),
      };
    });

  /** Wave 4C — lines from a small .xlsx/.csv, resolved by item code; unknown codes stay free text. */
  /** Wave 4C — which name a picked/imported item writes into a NEW line description. */
  const descLang = useDescriptionLanguage();
  const lineImport = useLineImport({
    id: 'sales-order',
    side: 'sales',
    describe: descLang.describe,
    price: (item) => tierPriceFor(item, saleTier),
    copyLines: () =>
      lines
        .filter((line) => line.itemId !== '' || line.description.trim() !== '')
        .map((line) => ({
          code: itemPicker.byId.get(line.itemId)?.code ?? '',
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
        })),
    visible: isDraft,
    gate: {
      permission: isNew ? 'sales.create' : 'sales.edit',
      reasonKey: isNew ? 'perm.cannot.createSalesOrders' : 'perm.cannot.editSalesOrders',
    },
    disabled: busy || !editable,
    onLines: (imported) =>
      setLines((current) => [
        ...current.filter(
        (line) =>
          line.itemId !== '' ||
          line.description.trim() !== '' ||
          line.qty.trim() !== '' ||
          line.unitPrice.trim() !== '',
      ),
        ...imported.map((line) => ({
          ...blankLine(),
          itemId: line.itemId,
          description: line.description,
          qty: line.qty,
          unitPrice: line.unitPrice,
          discountPct: line.discountPct,
          typedDescription: true,
          typedUnitPrice: true,
        })),
      ]),
  });

  const onSave = async () => {
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        const created = await createOrder.mutateAsync({
          partnerId,
          orderDate,
          ...(expectedDate ? { expectedDate } : {}),
          ...salesHeaderCreateBody(header),
          ...docCurrency.body(),
          ...rate.createBody(),
          ...commission.createBody(),
          ...(reference.trim() ? { reference: reference.trim() } : {}),
          ...(terms.trim() ? { terms } : {}),
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          ...(notes.trim() ? { notes } : {}),
        });
        void navigate(`/sales/orders/${created.id}/edit`, { replace: true });
      } else if (isDraft) {
        await updateOrder.mutateAsync({
          orderDate,
          expectedDate: expectedDate === '' ? null : expectedDate,
          ...salesHeaderPatchBody(header),
          ...docCurrency.body(),
          ...rate.patchBody(),
          ...commission.patchBody(),
          reference: reference.trim() === '' ? null : reference.trim(),
          terms: terms.trim() === '' ? null : terms,
          lines: requestLines(),
          ...(discount ? { discount: buildTwoWay(discount)! } : {}),
          ...(tax ? { tax: buildTwoWay(tax)! } : {}),
          notes: notes.trim() === '' ? null : notes,
        });
        loadedId.current = null;
      } else {
        await updateOrder.mutateAsync({
          expectedDate: expectedDate === '' ? null : expectedDate,
          notes: notes.trim() === '' ? null : notes,
        });
        loadedId.current = null;
      }
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403)
        denyPermission(isNew ? 'sales.create' : 'sales.edit');
      // The line above already reads the cause to spot a 403; this one used to
      // throw it away one expression later. `localizeServerCause` keeps it: a
      // refusal keeps the server's sentence localized (`ORDER_MESSAGES` are
      // mapped in STATIC_MESSAGE_KEYS), a request that never answered gets this
      // screen's own sentence rather than the browser's `Failed to fetch`. The
      // key is the EDITOR's, not the orders list's — two screens, two keys.
      setError(localizeServerCause(t, thrown, 'orders.errSaveFailedEditor'));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleting(false);
    if (!orderId) return;
    setBusy(true);
    setError(null);
    try {
      await deleteOrder.mutateAsync();
      void navigate('/sales/orders');
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('sales.delete');
      setError(localizeServerCause(t, thrown, 'orders.errSaveFailedEditor'));
    } finally {
      setBusy(false);
    }
  };

  if (!isNew && loading) {
    return <div className="h-64 animate-pulse rounded-xl bg-muted" aria-busy />;
  }

  return (
    <div className="grid gap-4">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 bg-background">
        <h2 className="text-lg font-bold">{t('orders.editorTitle')}</h2>
        <span className="rounded-md bg-muted px-2 py-1 font-mono text-sm text-muted-foreground">
          {order ? <bdi dir="ltr">{order.code}</bdi> : t('orders.codeNew')}
        </span>
        <OrderStatusChip status={status} />
        {/* The toolbar's acts, DECLARED — see `document-toolbar.tsx`. */}
        <DocumentToolbar
          header={{ kind: 'salesOrder', id: orderId, dirty }}
          actions={[
            {
              // Not PART 4.6's Cancel row: a link back to the list wearing the
              // same word. The primitive's header records the collision.
              id: 'back',
              row: null,
              label: t('common.cancel'),
              variant: 'outline',
              to: '/sales/orders',
            },
            {
              // Wave 2 — the invoice's Print/PDF pair on the sales order; see
              // the quotation's note. Saved documents only.
              id: 'printPdf',
              row: 'print',
              node:
                !isNew && order ? (
                  <DocumentActions
                    request={{ kind: 'sales_order', locale: printLocale, subjectId: order.id }}
                    payload={{ kind: 'sales_order', data: salesOrderDocument(order) }}
                  />
                ) : null,
            },
            {
              // Wave 4B — a NEW order may start FROM a quotation: the linking
              // conversion (unlike the invoice's Copy from, which only copies).
              id: 'fromQuotation',
              row: null,
              label: t('orders.fromQuotation'),
              icon: <FileInput aria-hidden />,
              variant: 'outline',
              visible: isNew,
              gate: { permission: 'sales.create', reasonKey: 'perm.cannot.convertQuotes' },
              disabled: busy,
              onAct: () => setQuotePicking(true),
            },
            // Wave 4C — Import lines / Download template (shared line tools).
            ...lineImport.acts,
            {
              // The document's question and the role's, kept apart (16.7).
              id: 'delete',
              row: 'delete',
              label: t('common.delete'),
              icon: <Trash2 aria-hidden />,
              variant: 'destructive',
              visible: !isNew && isDraft,
              gate: { permission: 'sales.delete', reasonKey: 'perm.cannot.deleteSalesOrders' },
              disabled: busy,
              onAct: () => setDeleting(true),
            },
            {
              id: 'save',
              row: 'save',
              label: t('common.save'),
              visible: isDraft || status !== 'cancelled',
              gate: {
                permission: isNew ? 'sales.create' : 'sales.edit',
                reasonKey: isNew
                  ? 'perm.cannot.createSalesOrders'
                  : 'perm.cannot.editSalesOrders',
              },
              busy,
              onAct: () => void onSave(),
            },
          ]}
        >
          {/* GEN-58 asks its sentence in the product’s ONE shared box
              (GEN-204, GEN-209 — "deleting a draft" is first on its list).
              `window.confirm` was neither of BR-PERM-15’s two patterns.
              Inside the same guard as the button, so the dialog cannot
              outlive the condition that allows the delete. */}
          {!isNew && isDraft ? (
            <DeleteConfirmBox
              open={deleting}
              detail={t('orders.confirmDelete')}
              onCancel={() => setDeleting(false)}
              onConfirm={() => void onDelete()}
              busy={busy}
            />
          ) : null}
          {/* Wave 4B's picker, inside the same guard as its button. */}
          {isNew && quotePicking ? (
            <QuotationConvertPicker
              partnerId={partnerId}
              onClose={() => setQuotePicking(false)}
              onPick={(quotationId) => void onConvertQuotation(quotationId)}
              busy={convertQuotation.isPending}
              error={quoteError}
            />
          ) : null}
        </DocumentToolbar>
      </div>

      {/* Wave 4B — the order's flow on its own form: the acts (the list
          page's, the same component) and its place in the chain. The rate
          and credit questions are awaited, so their boxes are mounted here,
          outside every condition that draws a button. */}
      <OrderDialogs order={order} acts={acts} screen="editor" />
      {!isNew && order ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('orders.formActions')}>
            <OrderActionButtons order={order} acts={acts} />
          </div>
          <OrderLifecycle order={order} acts={acts} />
        </div>
      ) : null}

      {!isDraft && status !== 'cancelled' ? (
        <p className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          {t('orders.confirmedNotice')} {t('orders.amendHint')}
        </p>
      ) : null}
      {acts.error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {acts.error}
        </p>
      ) : null}
      {status === 'cancelled' ? (
        <p className="rounded-md border border-red-300 bg-red-50/80 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {/* GEN-59's stored reason, read back OFF THE RECORD. Rows cancelled
              before the reason existed keep the old sentence. */}
          {order?.cancelReason != null
            ? t('orders.cancelledReasonNotice', { reason: order.cancelReason })
            : t('orders.cancelledNotice')}
        </p>
      ) : null}
      <OverStockStrip show={anyShort} />
      {/* Wave 4C — the line import's chooser, summary and per-row problems. */}
      {lineImport.panel}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {error}
        </p>
      ) : null}

      {/* The header box, foldable (2026-10-30). */}
      <CollapsibleHeaderCard
        storageKey="salesOrder"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        summary={{
          partner: order?.partnerName ?? partner?.name,
          date: orderDate,
          currency,
          code: order?.code,
          status: <OrderStatusChip status={status} />,
        }}
      >
          <div className="grid gap-1.5">
            <Label htmlFor="so-date">{t('common.date')}</Label>
            <Input
              id="so-date"
              type="date"
              dir="ltr"
              value={orderDate}
              onChange={(event) => setOrderDate(event.target.value)}
              disabled={!editable}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="so-expected">{t('orders.fExpected')}</Label>
            <Input
              id="so-expected"
              type="date"
              dir="ltr"
              value={expectedDate}
              onChange={(event) => setExpectedDate(event.target.value)}
              disabled={!editable && !liveOnly}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="so-customer">{t('sales.fCustomer')}</Label>
            {/* GEN-50, and BR-MD-01-07: OFFERS the active, DISPLAYS whoever
                this order already names. */}
            <PartnerTypeahead
              entity="customer"
              id="so-customer"
              label={t('sales.fCustomer')}
              value={partnerId || null}
              onChange={(id) => setPartnerId(id ?? '')}
              options={customerPicker.options}
              selected={customerPicker.optionFor(partnerId)}
              query={customerPicker.query}
              onQueryChange={customerPicker.setQuery}
              placeholder={t('sales.pickCustomer')}
              emptyLabel={t('picker.noMatchingCustomer')}
              loading={customerPicker.loading}
              disabled={!editable || !isNew}
            />
            {/* Wave 4B — the customer's balance and credit bar under the
                customer: the server's own figures (MD-01 field 29,
                BR-MD-01-09a/-09b), drawn here and decided nowhere else. */}
            <PartnerBalanceChip partner={partner} className="justify-self-start" />
          </div>
          <div className="grid gap-1.5">
            <Label>{t('sales.fCustomerId')}</Label>
            <div className="flex h-12 items-center gap-3 rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
              <Ltr value={order?.partnerCode ?? partner?.code ?? '—'} />
              <span className="ms-auto text-muted-foreground">
                <Ltr value={currency} />
              </span>
            </div>
          </div>
          {/* INV-F1…F6 + INV-F5 (SO-02): the invoice's header, the same
              component, the FxPanel in the grid rather than under the totals. */}
          <SalesHeaderFields
            idPrefix="so"
            partnerId={partnerId}
            state={header}
            onChange={(patch) => setHeader((current) => ({ ...current, ...patch }))}
            disabled={!editable}
            doc={order}
            rate={rate}
            commission={commission}
            currency={currency}
            currencyPicker={{ onChange: docCurrency.pick, disabled: currencyLocked }}
            warehouses={warehouses}
          />
          {/* Wave 4B — the customer's own reference / PO number; Convert to
              Order carries the quotation's field 13 here. */}
          <div className="grid gap-1.5">
            <Label htmlFor="so-ref">{t('orders.fReference')}</Label>
            <Input
              id="so-ref"
              value={reference}
              maxLength={50}
              onChange={(event) => setReference(event.target.value)}
              disabled={!editable}
            />
          </div>
          {/* Wave 4B — where the order came from, and which VERSION of the
              offer it carries (the quotation may have been revised since). */}
          {order?.sourceQuotationCode ? (
            <div className="grid gap-1.5">
              <Label>{t('orders.fSourceQuotation')}</Label>
              <div className="flex h-12 items-center rounded-[11px] border border-input bg-muted/40 px-3 font-mono text-sm">
                <bdi dir="ltr">
                  {order.sourceQuotationRevisionNo !== null
                    ? t('orders.sourceQuotationVersion', {
                        code: order.sourceQuotationCode,
                        rev: order.sourceQuotationRevisionNo,
                      })
                    : order.sourceQuotationCode}
                </bdi>
              </div>
            </div>
          ) : null}
      </CollapsibleHeaderCard>

      <Card>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-11 text-center">#</TableHead>
                <TableHead className="min-w-[160px]">{t('quotes.colItem')}</TableHead>
                <TableHead className="min-w-[140px]">{t('orders.colWarehouse')}</TableHead>
                <TableHead className="w-24">{t('sales.colUnit')}</TableHead>
                <TableHead className="min-w-[200px]">{t('sales.colDescription')}
                  {editable ? <DescriptionLangToggle language={descLang} /> : null}
                </TableHead>
                <TableHead className="w-24 text-end">{t('sales.colQty')}</TableHead>
                {bonusOn ? <TableHead className="w-24 text-end">{t('sales.colBonus')}</TableHead> : null}
                <TableHead className="w-32 text-end">{t('sales.colUnitPrice')}</TableHead>
                {discountOn ? <TableHead className="w-28 text-end">{t('sales.colDisc')}</TableHead> : null}
                <TableHead className="w-32 text-end">{t('sales.colTotal')}</TableHead>
                {/* Wave 4B — per-line delivery date for a staged order. */}
                <TableHead className="w-40" title={t('orders.deliveryDateHint')}>
                  {t('orders.colDeliveryDate')}
                </TableHead>
                {editable ? <TableHead className="w-11" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line, index) => {
                const view = totals.lines[index];
                return (
                  <TableRow key={line.key} className="odd:bg-muted/30">
                    <TableCell className="text-center font-mono text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <ItemCell
                        label={t('quotes.cellItem', { line: index + 1 })}
                        clearLabel={t('quotes.freeTextLine')}
                        picker={itemPicker}
                        value={line.itemId}
                        onPick={(itemId) => pickItem(line.key, itemId)}
                        disabled={!editable}
                      />
                      {/* Wave 4C — the customer's last price for this item, one click to use. */}
                      {partnerId && line.itemId ? (
                        <LastPriceHint
                          side="sales"
                          partnerId={partnerId}
                          itemId={line.itemId}
                          currency={currency}
                          currentPrice={line.unitPrice}
                          editable={editable}
                          line={index + 1}
                          onUse={(price) => setLine(line.key, { unitPrice: price, typedUnitPrice: true })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {line.itemId ? (
                        <NativeSelect
                          aria-label={t('orders.cellWarehouse', { line: index + 1 })}
                          className="h-9 text-sm"
                          value={line.warehouseId}
                          onChange={(event) => setLine(line.key, { warehouseId: event.target.value })}
                          disabled={!editable}
                        >
                          {/* INV-L3: empty = the header's default warehouse,
                              or no reservation when the header names none. */}
                          <option value="">
                            {header.warehouseId
                              ? ((warehouses ?? []).find((w) => w.id === header.warehouseId)?.name ??
                                t('orders.noReservation'))
                              : t('orders.noReservation')}
                          </option>
                          {(warehouses ?? [])
                            .filter((warehouse) => warehouse.isActive)
                            .map((warehouse) => (
                              <option key={warehouse.id} value={warehouse.id}>
                                {warehouse.name}
                              </option>
                            ))}
                        </NativeSelect>
                      ) : (
                        <span className="px-2 text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* INV-L4: the item's sell units; empty = its stock unit. */}
                      <LineUnitSelect
                        item={line.itemId ? itemPicker.byId.get(line.itemId) : undefined}
                        value={line.unitId}
                        onChange={(unitId) => setLine(line.key, { unitId })}
                        disabled={!editable}
                        label={t('sales.cellUnit', { line: index + 1 })}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        aria-label={t('sales.cellDescription', { line: index + 1 })}
                        value={line.description}
                        onChange={(event) =>
                          setLine(line.key, {
                            description: event.target.value,
                            typedDescription: true,
                          })
                        }
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellQty', { line: index + 1 })}
                        value={line.qty}
                        onChange={(event) => setLine(line.key, { qty: event.target.value })}
                        disabled={!editable}
                      />
                      <LineStockHint available={lineShort(line)} />
                    </TableCell>
                    {bonusOn ? (
                      <TableCell>
                        {/* INV-L1: free units given with the line. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellBonus', { line: index + 1 })}
                          value={line.bonusQty}
                          placeholder="0"
                          onChange={(event) => setLine(line.key, { bonusQty: event.target.value })}
                          disabled={!editable}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <CellInput
                        mono
                        dir="ltr"
                        inputMode="decimal"
                        aria-label={t('sales.cellUnitPrice', { line: index + 1 })}
                        value={line.unitPrice}
                        onChange={(event) =>
                          setLine(line.key, {
                            unitPrice: event.target.value,
                            typedUnitPrice: true,
                          })
                        }
                        disabled={!editable}
                      />
                      <BelowCostHint cost={lineBelowCost(line)} />
                    </TableCell>
                    {discountOn ? (
                      <TableCell>
                        {/* INV-L2: drawn only while the company's switch is on. */}
                        <CellInput
                          mono
                          dir="ltr"
                          inputMode="decimal"
                          aria-label={t('sales.cellDisc', { line: index + 1 })}
                          value={line.discountPct}
                          placeholder="0"
                          onChange={(event) => setLine(line.key, { discountPct: event.target.value })}
                          disabled={!editable}
                        />
                        {view?.discountAmount && view.discountAmount !== '0.00' ? (
                          <div className="pe-2 text-end text-xs text-muted-foreground">
                            <bdi dir="ltr">−{view.discountAmount}</bdi>
                          </div>
                        ) : null}
                      </TableCell>
                    ) : null}
                    <TableCell className="text-end">
                      {view?.lineTotal !== null && view?.lineTotal !== undefined ? (
                        <Amount value={view.lineTotal} currency={currency} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <CellInput
                        type="date"
                        dir="ltr"
                        aria-label={t('orders.cellDeliveryDate', { line: index + 1 })}
                        value={line.deliveryDate}
                        onChange={(event) => setLine(line.key, { deliveryDate: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    {editable ? (
                      <TableCell>
                        <button
                          type="button"
                          aria-label={t('sales.removeLine', { line: index + 1 })}
                          title={t('sales.removeLine', { line: index + 1 })}
                          onClick={() => removeLine(line.key)}
                          className="flex size-8 items-center justify-center rounded-md text-destructive transition-colors hover:bg-destructive/10"
                        >
                          <X className="size-4" aria-hidden />
                        </button>
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5}>
                  {editable ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-dashed"
                      onClick={() => setLines((current) => [...current, blankLine()])}
                    >
                      <Plus aria-hidden /> {t('sales.addLine')}
                    </Button>
                  ) : null}
                </TableCell>
                <TableCell className="text-end">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.qtyTotal')}
                  </span>{' '}
                  <bdi dir="ltr" className="font-mono font-semibold">
                    {totals.qtyTotal}
                  </bdi>
                </TableCell>
                <TableCell
                  colSpan={3 + (bonusOn ? 1 : 0) + (discountOn ? 1 : 0) + (editable ? 1 : 0)}
                  className="text-end"
                >
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                    {t('sales.subtotal')}
                  </span>{' '}
                  <Amount value={totals.subtotal} currency={currency} className="font-semibold" />
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardContent className="grid gap-3">
            {/* Wave 4B — the terms the order stands on (the quotation's W6,
                carried by Convert to Order). Draft-side: frozen once confirmed. */}
            <div className="grid gap-1.5">
              <Label htmlFor="so-terms">{t('orders.fTerms')}</Label>
              <Textarea
                id="so-terms"
                placeholder={t('quotes.termsPh')}
                maxLength={2000}
                value={terms}
                onChange={(event) => setTerms(event.target.value)}
                disabled={!editable}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="so-notes">{t('sales.notes')}</Label>
              <Textarea
                id="so-notes"
                maxLength={2000}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                disabled={!editable && !liveOnly}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardContent className="grid gap-3">
            {/* Wave 4C — the shared totals rows (F-2…F-7), with the currency code by the total. */}
            <DocumentTotals
              currency={currency}
              subtotal={totals.subtotal}
              discountAmount={totals.discountAmount}
              discountControl={
                <TwoWayPair
                  kind="discount"
                  state={discount}
                  onChange={setDiscount}
                  pct={totals.discountPct ?? '0.00'}
                  amount={totals.discountAmount ?? ''}
                  disabled={!editable}
                />
              }
              taxAmount={totals.taxAmount}
              vatControl={
                <TwoWayPair
                  kind="vat"
                  state={tax}
                  onChange={setTax}
                  pct={totals.taxPct ?? defaultTaxPct}
                  amount={totals.taxAmount ?? ''}
                  disabled={!editable}
                />
              }
              grandTotal={totals.grandTotal}
            />
            {/* PART 4: "a quotation raised on 2026-07-01, turned into an order
                on 2026-07-05, and invoiced on 2026-07-09 uses three different
                rates, and each document keeps its own." This is the order's
                own — it did not inherit the quotation's. Hidden entirely for a
                base-currency order (GEN-116). BR-MD-11-14's question is asked
                where the CONFIRMING click lives, which for an order is the
                list screen's Confirm, not here. */}
            {/* The FxPanel now sits in the header's Currency & rate cell (INV-F5). */}
            {/* Wave 4B — the margin, for a reader allowed to see cost. */}
            <MarginPanel
              lines={lines}
              items={itemPicker.byId}
              net={totals.subtotal !== null ? netAmount(totals.subtotal, totals.discountAmount, currency) : null}
              currency={currency}
            />
            {/* SAL-02 §12: an order posts nothing. */}
            <p className="text-xs text-muted-foreground">{t('orders.noPostingNote')}</p>
          </CardContent>
        </Card>
      </div>
      {/* Wave 2 — INV-H5's files panel on this document. Hidden
          behind "save first" on a new draft; permission follows the record. */}
      <AttachmentsPanel kind="sales_order" entityId={orderId} />
    </div>
  );
}
